import React, { useMemo, useState, useCallback } from "react";
import DataGrid from "../../../widgets/DataGrid/DataGrid";
import { TextField, MenuItem, Box } from "@mui/material";

export const PaymentsTable = ({ rows = [], onRowClick, loading = false }) => {
  const formatDisplay = (date) =>
    date ? new Date(date).toLocaleDateString() : "—";

  const toISO = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  const uniqueFrequencies = useMemo(
    () => Array.from(new Set(rows.map((r) => r.frequency).filter(Boolean))),
    [rows]
  );

  const [tableFilter, setTableFilter] = useState({
    customerName: "",
    paymentMethodDisplay: "",
    frequency: "",
    startDate: "", // ISO YYYY-MM-DD
    endDate: "",   // ISO YYYY-MM-DD
    amountMin: "",
    amountMax: "",
    paymentsCountMin: "",
    paymentsCountMax: "",
  });

  const columns = useMemo(
    () => [
      { field: "customerName", headerName: "Customers", sortable: true, numberSort: false },
      { field: "paymentMethodDisplay", headerName: "Payment Method", sortable: false, numberSort: false },
      { field: "amount", headerName: "Amount", sortable: true, numberSort: true },
      { field: "frequency", headerName: "Frequency", sortable: true, numberSort: false },
      { field: "startDate", headerName: "Start Date", sortable: true, numberSort: false },
      { field: "endDate", headerName: "End Date", sortable: true, numberSort: false },
      { field: "paymentsCount", headerName: "Count of payments", sortable: true, numberSort: true },
    ],
    []
  );

  const filters = useMemo(
    () => ({
      customerName: (
        <TextField
          size="small"
          fullWidth
          placeholder="Search name, email, or company"
          value={tableFilter.customerName}
          onChange={(e) =>
            setTableFilter((s) => ({ ...s, customerName: e.target.value }))
          }
        />
      ),
      paymentMethodDisplay: (
        <TextField
          size="small"
          fullWidth
          placeholder="Search card brand/bank/last4"
          value={tableFilter.paymentMethodDisplay}
          onChange={(e) =>
            setTableFilter((s) => ({
              ...s,
              paymentMethodDisplay: e.target.value,
            }))
          }
        />
      ),
      amount: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            type="number"
            placeholder="Min"
            value={tableFilter.amountMin}
            onChange={(e) =>
              setTableFilter((s) => ({ ...s, amountMin: e.target.value }))
            }
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            fullWidth
            type="number"
            placeholder="Max"
            value={tableFilter.amountMax}
            onChange={(e) =>
              setTableFilter((s) => ({ ...s, amountMax: e.target.value }))
            }
            inputProps={{ min: 0 }}
          />
        </Box>
      ),
      frequency: (
        <TextField
          select
          size="small"
          label="Frequency"
          value={tableFilter.frequency}
          onChange={(e) =>
            setTableFilter((s) => ({ ...s, frequency: e.target.value }))
          }
          fullWidth
        >
          <MenuItem value="">All</MenuItem>
          {uniqueFrequencies.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </TextField>
      ),
      // Real date inputs (NOT selects)
      startDate: (
        <TextField
          type="date"
          size="small"
          label="Start Date"
          value={tableFilter.startDate}
          onChange={(e) =>
            setTableFilter((s) => ({ ...s, startDate: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      ),
      endDate: (
        <TextField
          type="date"
          size="small"
          label="End Date"
          value={tableFilter.endDate}
          onChange={(e) =>
            setTableFilter((s) => ({ ...s, endDate: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      ),
      paymentsCount: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            type="number"
            placeholder="Min"
            value={tableFilter.paymentsCountMin}
            onChange={(e) =>
              setTableFilter((s) => ({
                ...s,
                paymentsCountMin: e.target.value,
              }))
            }
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            fullWidth
            type="number"
            placeholder="Max"
            value={tableFilter.paymentsCountMax}
            onChange={(e) =>
              setTableFilter((s) => ({
                ...s,
                paymentsCountMax: e.target.value,
              }))
            }
            inputProps={{ min: 0 }}
          />
        </Box>
      ),
    }),
    [
      tableFilter.customerName,
      tableFilter.paymentMethodDisplay,
      tableFilter.amountMin,
      tableFilter.amountMax,
      tableFilter.frequency,
      tableFilter.startDate,
      tableFilter.endDate,
      tableFilter.paymentsCountMin,
      tableFilter.paymentsCountMax,
      uniqueFrequencies,
    ]
  );

  const mappedRows = useMemo(() => {
    return rows
      .map((r) => {
        const startISO = toISO(r.startDate);
        const endISO = toISO(r.endDate);
        return {
          ...r,
          id: r.id || r._id, // DataGrid expects an id
          paymentMethodDisplay:
            r.paymentMethod?.type === "card"
              ? `${r.paymentMethod.brand} •••• ${r.paymentMethod.last4}`
              : `${r.paymentMethod?.bankName ?? "Bank"} •••• ${r.paymentMethod?.last4 ?? ""}`,
          // Display values (localized)
          startDate: formatDisplay(r.startDate),
          endDate: formatDisplay(r.endDate),
          // Keep ISO for filtering
          __startISO: startISO,
          __endISO: endISO,
          amount: Number(r.amount),
          paymentsCount: Number(r.paymentsCount ?? 0),
        };
      })
      .filter((r) => {
        const matchCustomer =
          !tableFilter.customerName ||
          (r.customerName || "")
            .toLowerCase()
            .includes(tableFilter.customerName.toLowerCase());

        const matchPM =
          !tableFilter.paymentMethodDisplay ||
          (r.paymentMethodDisplay || "")
            .toLowerCase()
            .includes(tableFilter.paymentMethodDisplay.toLowerCase());

        const matchFreq =
          !tableFilter.frequency || r.frequency === tableFilter.frequency;

        // Date equality against ISO (picker returns YYYY-MM-DD)
        const matchStart =
          !tableFilter.startDate || r.__startISO === tableFilter.startDate;

        const matchEnd =
          !tableFilter.endDate || r.__endISO === tableFilter.endDate;

        const minAmt =
          tableFilter.amountMin !== "" ? parseFloat(tableFilter.amountMin) : null;
        const maxAmt =
          tableFilter.amountMax !== "" ? parseFloat(tableFilter.amountMax) : null;
        const matchAmount =
          (minAmt === null || (!Number.isNaN(minAmt) && r.amount >= minAmt)) &&
          (maxAmt === null || (!Number.isNaN(maxAmt) && r.amount <= maxAmt));

        const minCnt =
          tableFilter.paymentsCountMin !== ""
            ? parseInt(tableFilter.paymentsCountMin, 10)
            : null;
        const maxCnt =
          tableFilter.paymentsCountMax !== ""
            ? parseInt(tableFilter.paymentsCountMax, 10)
            : null;
        const matchCount =
          (minCnt === null || (!Number.isNaN(minCnt) && r.paymentsCount >= minCnt)) &&
          (maxCnt === null || (!Number.isNaN(maxCnt) && r.paymentsCount <= maxCnt));

        return (
          matchCustomer &&
          matchPM &&
          matchFreq &&
          matchStart &&
          matchEnd &&
          matchAmount &&
          matchCount
        );
      });
  }, [rows, tableFilter]);

  // Keep drawer open-on-click working regardless of what DataGrid passes back
  const handleShowDetails = useCallback(
    (arg) => {
      const row = arg?.row || arg?.data || arg; // be tolerant about signature
      const customerId = row?.customerId ?? row?.id;
      if (onRowClick && customerId != null) onRowClick(customerId, row);
    },
    [onRowClick]
  );

  return (
    <DataGrid
      columns={columns}
      rows={mappedRows}
      filters={filters}
      loading={loading}
      showDetails={handleShowDetails}
    />
  );
};
